import type { MarketingRouterRule } from '~/types/marketingRouterRule';

import { toMatchValues } from './marketingRouterMatch';

type Match = MarketingRouterRule['match'];

/**
 * How each match field reads in the summary sentence, in the order the
 * sentence says them. `regions` leads because it is where the visitor is;
 * the UTM fields then describe how they arrived, and `url` where they landed.
 */
const CLAUSES: Array<[keyof Match, (values: string) => string]> = [
  ['regions', (values) => `in ${values}`],
  ['utm_source', (values) => `from ${values}`],
  ['utm_medium', (values) => `via ${values}`],
  ['utm_campaign', (values) => `on campaign ${values}`],
  ['utm_term', (values) => `on term ${values}`],
  ['utm_content', (values) => `with content ${values}`],
  ['url', (values) => `landing on ${values}`],
];

/**
 * Joins a field's values the way the field matches them: any one value is
 * enough, so they read as alternatives rather than a list of requirements.
 */
function formatAlternatives(values: Array<string>): string {
  if (values.length <= 1) {
    return values.join('');
  }

  return `${values.slice(0, -1).join(', ')} or ${values[values.length - 1]}`;
}

/**
 * A one-sentence description of the traffic a match will select.
 *
 * Values are quoted exactly as configured rather than tidied into prose: the
 * router compares them literally, so a summary that title-cases `google` would
 * describe traffic the rule does not actually match.
 *
 * Returns null while the match names no traffic at all, so the form says
 * nothing rather than claiming a rule matches everyone.
 */
export function getMatchSummary(match: Match): string | null {
  const clauses = CLAUSES.flatMap(([field, toClause]) => {
    const values = toMatchValues(match[field]);

    return values.length === 0 ? [] : [toClause(formatAlternatives(values))];
  });

  if (clauses.length === 0) {
    return null;
  }

  return `This rule will match visitors ${clauses.join(', ')}.`;
}
